import React from 'react';
import { Crown, TrendingUp, Users, Star, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useTranslation } from 'react-i18next';

const Services = () => {
  const { t } = useTranslation();
  
  const iconComponents = {
    Crown, TrendingUp, Users, Star
  };

  const services = [
    { ...t('services.programs.executive', { returnObjects: true }), icon: 'Crown' },
    { ...t('services.programs.leadership', { returnObjects: true }), icon: 'TrendingUp', popular: true },
    { ...t('services.programs.team', { returnObjects: true }), icon: 'Users' },
    { ...t('services.programs.highPotential', { returnObjects: true }), icon: 'Star' }
  ];

  return (
    <section id="services" className="py-24 bg-gray-50">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('services.title')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('services.subtitle')}
          </p>
        </div>
        
        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {services.map((service) => {
            const Icon = iconComponents[service.icon];
            
            return (
              <Card 
                key={service.title} 
                className={`relative overflow-hidden border-2 border-gray-200 bg-white hover:border-blue-400 hover:shadow-xl transition-all duration-300 group ${service.popular ? 'ring-2 ring-blue-500 ring-offset-4' : ''}`}
              >
                {service.popular && (
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-blue-600 text-white px-3 py-1">
                      {t('services.mostPopular')}
                    </Badge>
                  </div>
                )}
                
                <CardHeader>
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-xl bg-blue-50 text-blue-600 mb-4 shadow-md group-hover:scale-110 transition-transform duration-300">
                    <Icon className="w-8 h-8" />
                  </div>
                  
                  <CardTitle className="text-2xl mb-2 text-gray-900">{service.title}</CardTitle>
                  <CardDescription className="text-base text-gray-600">{service.description}</CardDescription>
                </CardHeader>
                
                <CardContent>
                  <div className="space-y-3">
                    {service.features && service.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center mt-0.5">
                          <Check className="w-3 h-3" />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <button 
                    onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                    className={`mt-6 w-full py-3 rounded-lg font-semibold transition-all duration-300 ${
                      service.popular 
                        ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                    }`}
                  >
                    {t('services.learnMore')}
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
